/******************************************************************************
 * Created Date: N/A                                                          *
 * Author: Sean W.                                                            *
 * -----                                                                      *
 * Last Modified: Thu Dec 09 2021                                             *
 * Modified By: Patrick B.                                                    *
 * -----                                                                      *
 * File: /src/components/common/dialogs/DeleteDialog.js                       *
 * Copyright (c) 2021 Kuva                                                    *
 ******************************************************************************/

import { useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Checkbox,
  Typography
} from "@mui/material";
import Draggable from "react-draggable";

function PaperComponent(props) {
  return (
    <Draggable
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} />
    </Draggable>
  );
}

/** @function DeleteDialog
 * The Delete Dialog returns a draggable dialog that confirms a delete function for processed frames.
 * 
 * @param {boolean} show: determines when the dialog box should display
   @param {func} onDelete: function to be called when 'Delete' button is pressed
   @param {func} onCancel: function to be called when 'Cancel' button is pressed
   @param {string} tile: header for the dialog box
   @param {string} text: text for the dialog box
   @param {string} hidePromptAction: used to determine whether or not to display the option of hiding this dialog. (saves to sessionStorage)
 */

export default function DeleteDialog(props) {
  const [checked, setChecked] = useState(false);

  const handleChange = e => {
    setChecked(e.target.checked);
  };

  const handleCloseDialog = () => {
    setChecked(false);
    props.onCancel();
  };

  const handleDelete = () => {
    if (props.hidePromptAction && checked) {
      sessionStorage.setItem(props.hidePromptAction, "hide");
    }
    props.onDelete();
  };

  return (
    <div>
      <Dialog
        open={props.show}
        onClose={handleCloseDialog}
        PaperComponent={PaperComponent}
        aria-labelledby="draggable-dialog-title"
      >
        <DialogTitle style={{ cursor: "move" }} id="draggable-dialog-title">
          {props.tile}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>{props.text}</DialogContentText>
        </DialogContent>
        {props.hidePromptAction && (
          <DialogContent
            style={{
              display: "flex",
              alignItems: "center"
            }}
          >
            <Checkbox
              style={{
                width: 0,
                marginRight: 8
              }}
              checked={checked}
              onChange={handleChange}
              inputProps={{ "aria-label": "controlled" }}
            />
            <Typography>Do not show this warning again</Typography>
          </DialogContent>
        )}
        <DialogActions>
          <Button
            role="closeDeleteDialog"
            autoFocus
            onClick={handleCloseDialog}
            color="primary"
          >
            Cancel
          </Button>
          <Button
            role="deleteButtonDialog"
            onClick={handleDelete}
            color="primary"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

DeleteDialog.propTypes = {
  show: PropTypes.bool,
  tile: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  handleClose: PropTypes.func,
  onCancel: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  hidePromptAction: PropTypes.string
};
